import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { tokens } from '@/design';
import {
  type AssetTreeNode,
  type AssetShellRecord,
  type TenantRecord,
  GTWIN_API_BASE,
  associateAssetLayer,
  createAsset,
  fetchAssetTree,
  fetchScopeManifest,
  listLor3Assets,
  listTenants,
  updateAsset,
} from './estateClient';
import { listEmittedLayers, type EmittedLayerRecord, type EmittedLayerType } from './converterOutputClient';
import { EstateBasemapPanel, type EstateMapTool } from './EstateBasemapPanel';
import './EstateAdminRoute.css';

type ScreenId = 'assets' | 'layers' | 'viewer' | 'projects' | 'handover';

type AssetRepresentation = {
  type: 'point' | 'rectangle' | 'square' | 'polygon' | 'area' | 'corridor' | 'path';
  lon: number;
  lat: number;
  labelLon?: number;
  labelLat?: number;
  coordinates?: [number, number][];
  width?: number;
  height?: number;
  rotation?: number;
  strokeColor?: string;
  fillColor?: string;
  fillOpacity?: number;
  strokeWidth?: number;
  zIndex?: number;
  pointShape?: 'circle' | 'square' | 'diamond' | 'triangle';
};

type LayerAssociationStatus = 'independent' | 'project-linked' | 'asset-associated';
type LayerViewMode = 'list' | 'cards';
type LayerDateFilter = 'all' | '24h' | '7d' | '30d';

type GtwinProjectRecord = {
  id: string;
  label: string;
  domain: string;
  updatedAt: string | null;
  layerCount: number;
};

function flatten(nodes: AssetTreeNode[]): AssetTreeNode[] {
  return nodes.flatMap((n) => [n, ...flatten(n.children ?? [])]);
}

function findNode(nodes: AssetTreeNode[], id: string): AssetTreeNode | null {
  for (const n of nodes) {
    if (n.id === id) return n;
    const c = findNode(n.children ?? [], id);
    if (c) return c;
  }
  return null;
}

function syncConverterTenantScope(tenantId: string) {
  try {
    const raw = window.localStorage.getItem('geoconverter.storageScopeContext');
    const prior = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    window.localStorage.setItem(
      'geoconverter.storageScopeContext',
      JSON.stringify({ ...prior, tenantId }),
    );
  } catch { /* browser policy */ }
}

function defaultNodeType(lor: number): string {
  if (lor === 0) return 'portfolio';
  if (lor === 1) return 'business-unit';
  if (lor === 2) return 'field';
  return 'platform';
}

function bboxFromRepresentation(rep: AssetRepresentation): number[] | null {
  if (rep.coordinates?.length) {
    const xs = rep.coordinates.map((point) => point[0]);
    const ys = rep.coordinates.map((point) => point[1]);
    return [
      Number(Math.min(...xs).toFixed(6)),
      Number(Math.min(...ys).toFixed(6)),
      Number(Math.max(...xs).toFixed(6)),
      Number(Math.max(...ys).toFixed(6)),
    ];
  }
  if (!Number.isFinite(rep.lon) || !Number.isFinite(rep.lat)) return null;
  const w = rep.type === 'point' ? 0.01 : Math.max(Number(rep.width ?? 0.05), 0.005);
  const h = rep.type === 'point' ? 0.01 : Math.max(Number(rep.height ?? rep.width ?? 0.05), 0.005);
  return [
    Number((rep.lon - w / 2).toFixed(6)),
    Number((rep.lat - h / 2).toFixed(6)),
    Number((rep.lon + w / 2).toFixed(6)),
    Number((rep.lat + h / 2).toFixed(6)),
  ];
}

function lorLabel(lor: number): string {
  return `LoR${lor}`;
}

function countProjectLayers(payload: unknown): number {
  const scene = ((payload as { geospatial?: { scene?: Record<string, unknown> } } | null)?.geospatial?.scene ?? {}) as Record<string, unknown>;
  return ['basemaps', 'realityData', 'engineeringModels', 'featureLayers', 'overlays']
    .reduce((count, slot) => count + (Array.isArray(scene[slot]) ? scene[slot].length : 0), scene.terrain ? 1 : 0);
}

function layerRefValue(ref: Record<string, unknown>, key: string): string {
  const metadata = ref.metadata as Record<string, unknown> | undefined;
  const value = ref[key] ?? metadata?.[key];
  return typeof value === 'string' ? value : '';
}

function layerMatchesRef(layer: EmittedLayerRecord, ref: Record<string, unknown>): boolean {
  const candidates = [
    layerRefValue(ref, 'sourceRunId'),
    layerRefValue(ref, 'runId'),
    layerRefValue(ref, 'outputPath'),
    layerRefValue(ref, 'path'),
    layerRefValue(ref, 'url'),
    layerRefValue(ref, 'artifactUrl'),
  ].filter(Boolean);
  return candidates.some((value) => value === layer.id || value === layer.path || value === layer.url);
}

function layerStatusFor(layer: EmittedLayerRecord, assets: AssetTreeNode[]): LayerAssociationStatus {
  const refs = assets.flatMap((asset) =>
    ((asset.layer_refs ?? []) as Array<Record<string, unknown>>).map((ref) => ({ asset, ref })),
  );
  const match = refs.find(({ ref }) => layerMatchesRef(layer, ref));
  if (!match) return 'independent';
  const hasAsset = match.asset.lor === 3;
  const hasProject = Boolean(layerRefValue(match.ref, 'gtwinId') || layerRefValue(match.ref, 'gtwin_id'));
  if (hasAsset) return 'asset-associated';
  return hasProject ? 'project-linked' : 'independent';
}

function associatedAssetFor(layer: EmittedLayerRecord, assets: AssetTreeNode[]): AssetTreeNode | null {
  return assets.find((asset) =>
    ((asset.layer_refs ?? []) as Array<Record<string, unknown>>).some((ref) => layerMatchesRef(layer, ref)),
  ) ?? null;
}

function withinDateFilter(layer: EmittedLayerRecord, filter: LayerDateFilter): boolean {
  if (filter === 'all') return true;
  if (!layer.mtime) return false;
  const timestamp = Date.parse(layer.mtime);
  if (!Number.isFinite(timestamp)) return false;
  const ageMs = Date.now() - timestamp;
  const maxMs = filter === '24h' ? 24 * 60 * 60 * 1000 : filter === '7d' ? 7 * 24 * 60 * 60 * 1000 : 30 * 24 * 60 * 60 * 1000;
  return ageMs <= maxMs;
}

function statusLabel(status: LayerAssociationStatus): string {
  if (status === 'asset-associated') return 'Asset-associated';
  if (status === 'project-linked') return 'Project-linked';
  return 'Independent';
}

async function listGtwinProjects(tenantId: string): Promise<GtwinProjectRecord[]> {
  const url = `${GTWIN_API_BASE}/api/v1/gtwins?domain=geospatial&limit=100`;
  const res = await fetch(url, { headers: { 'X-Tenant-Id': tenantId } });
  if (!res.ok) throw new Error(`gTwin project API ${res.status}: ${await res.text()}`);
  const body = (await res.json()) as { features?: Array<{ id: string; properties?: Record<string, unknown> }> };
  return (body.features ?? []).map((feature) => {
    const properties = feature.properties ?? {};
    const envelope = (properties.envelope ?? {}) as Record<string, unknown>;
    const payload = properties.payload;
    const label = String(envelope.label ?? properties.title ?? feature.id);
    return {
      id: feature.id,
      label,
      domain: String(properties.domain ?? envelope.domain ?? 'geospatial'),
      updatedAt: typeof envelope.updatedAt === 'string' ? envelope.updatedAt : null,
      layerCount: countProjectLayers(payload),
    };
  });
}

/* ── Tree node component ───────────────────────── */

function TreeNode({
  node,
  selectedId,
  onSelect,
  openNodes,
  onToggle,
}: {
  node: AssetTreeNode;
  selectedId: string | null;
  onSelect: (id: string) => void;
  openNodes: Set<string>;
  onToggle: (id: string) => void;
}) {
  const hasChildren = (node.children?.length ?? 0) > 0;
  const isOpen = openNodes.has(node.id);
  const isSelected = node.id === selectedId;

  return (
    <div className={`estate-node${isOpen ? ' open' : ''}`}>
      <button
        type="button"
        className={`estate-node-row${isSelected ? ' selected' : ''}`}
        onClick={() => onSelect(node.id)}
      >
        <span className="estate-node-arrow" onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggle(node.id); }}>
          {hasChildren && <span className="material-symbols-outlined">chevron_right</span>}
        </span>
        <span className="estate-node-tile" data-lor={Math.min(4, node.lor ?? 0)}>
          {lorLabel(node.lor)}
        </span>
        <span className="estate-node-main">
          <strong>{node.label}</strong>
          <small>{node.node_type || 'asset'} · {node.path || node.id}</small>
        </span>
        <span className="estate-node-right">
          {(node.layer_refs as unknown[])?.length > 0 && (
            <span className="estate-node-badge">{(node.layer_refs as unknown[]).length} layer</span>
          )}
          {hasChildren && <span className="estate-node-count">{node.children!.length}</span>}
        </span>
      </button>
      {hasChildren && isOpen && (
        <div className="estate-node-children">
          {node.children!.map((c) => (
            <TreeNode
              key={c.id}
              node={c}
              selectedId={selectedId}
              onSelect={onSelect}
              openNodes={openNodes}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main component ────────────────────────────── */

export function EstateAdminRoute() {
  const navigate = useNavigate();
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [screen, setScreen] = useState<ScreenId>('assets');
  const [globalNavOpen, setGlobalNavOpen] = useState(false);
  const [treeCollapsed, setTreeCollapsed] = useState(false);
  const [mapConfigOpen, setMapConfigOpen] = useState(false);
  const [toolPaletteOpen, setToolPaletteOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tenants, setTenants] = useState<TenantRecord[]>([]);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  const [tree, setTree] = useState<AssetTreeNode[]>([]);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [mapTool, setMapTool] = useState<EstateMapTool>('select');
  const [basemap, setBasemap] = useState<'none' | 'osm' | 'carto-positron' | 'carto-dark-matter' | 'esri-imagery'>('osm');
  const [openNodes, setOpenNodes] = useState<Set<string>>(new Set());

  const [newAsset, setNewAsset] = useState({
    label: '', lor: 3, parent_id: '', node_type: 'platform',
    domain: '', axis: 'spatial', boundary_owner: 'admin-registry', path_desc: '',
  });
  const [projectLabel, setProjectLabel] = useState('');

  const [geoShape, setGeoShape] = useState<AssetRepresentation['type']>('corridor');
  const [geoLat, setGeoLat] = useState('');
  const [geoLon, setGeoLon] = useState('');
  const [geoWidth, setGeoWidth] = useState('5');
  const [geoHeight, setGeoHeight] = useState('22');
  const [geoRotation, setGeoRotation] = useState('35');
  const [geoOrder, setGeoOrder] = useState('4');
  const [geoStrokeWidth, setGeoStrokeWidth] = useState('2');
  const [geoFillOpacity, setGeoFillOpacity] = useState('0.14');
  const [geoStrokeColor, setGeoStrokeColor] = useState<string>(tokens.color.accent.primary);
  const [geoFillColor, setGeoFillColor] = useState<string>(tokens.color.accent.primary);
  const [geoPointShape, setGeoPointShape] = useState<NonNullable<AssetRepresentation['pointShape']>>('circle');
  const [estateProfile, setEstateProfile] = useState('oil-gas');
  const [layerView, setLayerView] = useState<LayerViewMode>('list');
  const [layerQuery, setLayerQuery] = useState('');
  const [layerTypeFilter, setLayerTypeFilter] = useState<'all' | EmittedLayerType>('all');
  const [layerStatusFilter, setLayerStatusFilter] = useState<'all' | LayerAssociationStatus>('all');
  const [layerDateFilter, setLayerDateFilter] = useState<LayerDateFilter>('all');
  const [emittedLayers, setEmittedLayers] = useState<EmittedLayerRecord[]>([]);
  const [gtwinProjects, setGtwinProjects] = useState<GtwinProjectRecord[]>([]);
  const [lor3Assets, setLor3Assets] = useState<AssetShellRecord[]>([]);
  const [layersLoading, setLayersLoading] = useState(false);
  const [layerError, setLayerError] = useState<string | null>(null);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [associationOpen, setAssociationOpen] = useState(false);
  const [associationProjectId, setAssociationProjectId] = useState('');
  const [associationAssetId, setAssociationAssetId] = useState('');

  const activeTenant = useMemo(
    () => tenants.find((t) => t.id === activeTenantId) ?? null,
    [activeTenantId, tenants],
  );
  const basemapLabel = useMemo(() => {
    if (basemap === 'none') return 'Designer canvas';
    if (basemap === 'osm') return 'OpenStreetMap';
    if (basemap === 'carto-positron') return 'Carto Positron';
    if (basemap === 'carto-dark-matter') return 'Carto Dark Matter';
    return 'ESRI Imagery';
  }, [basemap]);
  const flatTree = useMemo(() => flatten(tree), [tree]);
  const selectedAsset = useMemo(
    () => (selectedAssetId ? findNode(tree, selectedAssetId) : null),
    [selectedAssetId, tree],
  );
  const filteredTree = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return tree;
    const keep = (n: AssetTreeNode): AssetTreeNode | null => {
      const children = (n.children ?? []).map(keep).filter(Boolean) as AssetTreeNode[];
      const match = [n.label, n.node_type, n.path, `lor${n.lor}`].join(' ').toLowerCase().includes(term);
      return match || children.length ? { ...n, children } : null;
    };
    return tree.map(keep).filter(Boolean) as AssetTreeNode[];
  }, [filter, tree]);

  const loadTenants = useCallback(async () => {
    const rows = await listTenants();
    setTenants(rows);
    setActiveTenantId((cur) => cur ?? rows[0]?.id ?? null);
  }, []);

  const loadTree = useCallback(async (tenantId: string) => {
    const rows = await fetchAssetTree(tenantId);
    setTree(rows);
    const all = flatten(rows);
    setSelectedAssetId((cur) => cur ?? all.find((r) => r.lor === 3)?.id ?? all[0]?.id ?? null);
    const defaultOpen = new Set<string>();
    for (const n of all) {
      if ((n.children?.length ?? 0) > 0 && (n.lor ?? 0) < 3) defaultOpen.add(n.id);
    }
    setOpenNodes(defaultOpen);
  }, []);

  const loadLayerManagementData = useCallback(async (tenantId: string) => {
    setLayersLoading(true);
    setLayerError(null);
    try {
      const [projectsRows, emittedRows, lor3Rows] = await Promise.all([
        listGtwinProjects(tenantId),
        listEmittedLayers(),
        listLor3Assets(tenantId),
      ]);
      setGtwinProjects(projectsRows);
      setEmittedLayers(emittedRows);
      setLor3Assets(lor3Rows);
      setSelectedLayerId((current) => current ?? emittedRows[0]?.id ?? null);
      setAssociationProjectId((current) => current || projectsRows[0]?.id || activeTenant?.estate_gtwin_id || '');
      setAssociationAssetId((current) => current || selectedAssetId || lor3Rows[0]?.id || '');
    } catch (err) {
      setLayerError(err instanceof Error ? err.message : 'Failed to load layer management data');
    } finally {
      setLayersLoading(false);
    }
  }, [activeTenant?.estate_gtwin_id, selectedAssetId]);

  useEffect(() => {
    loadTenants().catch((err: Error) => setError(err.message));
  }, [loadTenants]);

  useEffect(() => {
    if (!activeTenantId) return;
    syncConverterTenantScope(activeTenantId);
    loadTree(activeTenantId).catch((err: Error) => setError(err.message));
  }, [activeTenantId, loadTree]);

  useEffect(() => {
    if (!activeTenantId || screen !== 'layers') return;
    loadLayerManagementData(activeTenantId).catch((err: Error) => setLayerError(err.message));
  }, [activeTenantId, loadLayerManagementData, screen]);

  useEffect(() => {
    if (screen !== 'layers') return;
    if (selectedAsset?.lor === 3) setAssociationAssetId(selectedAsset.id);
  }, [screen, selectedAsset]);

  useEffect(() => {
    const rep = selectedAsset?.representation as Partial<AssetRepresentation> | null | undefined;
    if (!rep) return;
    if (rep.type) setGeoShape(rep.type);
    if (typeof rep.lat === 'number') setGeoLat(String(rep.lat));
    if (typeof rep.lon === 'number') setGeoLon(String(rep.lon));
    if (typeof rep.width === 'number') setGeoWidth(String(Number((rep.width * 100).toFixed(2))));
    if (typeof rep.height === 'number') setGeoHeight(String(Number((rep.height * 100).toFixed(2))));
    if (typeof rep.rotation === 'number') setGeoRotation(String(rep.rotation));
    if (typeof rep.zIndex === 'number') setGeoOrder(String(rep.zIndex));
    if (typeof rep.strokeWidth === 'number') setGeoStrokeWidth(String(rep.strokeWidth));
    if (typeof rep.fillOpacity === 'number') setGeoFillOpacity(String(rep.fillOpacity));
    if (rep.strokeColor) setGeoStrokeColor(rep.strokeColor);
    if (rep.fillColor) setGeoFillColor(rep.fillColor);
    if (rep.pointShape) setGeoPointShape(rep.pointShape);
  }, [selectedAsset]);

  useEffect(() => {
    if (!toast) return;
    const h = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(h);
  }, [toast]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setMapTool('select');
      setToolPaletteOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  function toggleNode(id: string) {
    setOpenNodes((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function expandAll() {
    setOpenNodes(new Set(flatTree.filter((n) => (n.children?.length ?? 0) > 0).map((n) => n.id)));
  }

  function collapseAll() {
    setOpenNodes(new Set());
  }

  function applyGeometryToAsset() {
    if (!selectedAssetId || !activeTenantId) return;
    const lat = parseFloat(geoLat);
    const lon = parseFloat(geoLon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      setToast('Set lat/lon first (click map or enter coordinates)');
      return;
    }
    const existing = selectedAsset?.representation as Partial<AssetRepresentation> | null | undefined;
    const rep: AssetRepresentation = {
      type: geoShape,
      lat, lon,
      coordinates: existing?.type === geoShape ? existing.coordinates : undefined,
      width: parseFloat(geoWidth) / 100 || 0.05,
      height: geoShape === 'square'
        ? parseFloat(geoWidth) / 100 || 0.05
        : parseFloat(geoHeight) / 100 || 0.05,
      rotation: parseFloat(geoRotation) || 0,
      strokeColor: geoStrokeColor,
      fillColor: geoFillColor,
      fillOpacity: parseFloat(geoFillOpacity) || 0.14,
      strokeWidth: parseFloat(geoStrokeWidth) || 2,
      zIndex: parseInt(geoOrder, 10) || 4,
      pointShape: geoPointShape,
    };
    void handleRepresentationChange(selectedAssetId, rep);
  }

  function clearDrawnVertices() {
    if (!selectedAssetId || !selectedAsset?.representation) return;
    const existing = selectedAsset.representation as Partial<AssetRepresentation>;
    const fallbackLat = Number.isFinite(parseFloat(geoLat)) ? parseFloat(geoLat) : 0;
    const fallbackLon = Number.isFinite(parseFloat(geoLon)) ? parseFloat(geoLon) : 0;
    const fallbackWidth = Number.isFinite(parseFloat(geoWidth)) ? parseFloat(geoWidth) / 100 : 0.05;
    const fallbackHeight = Number.isFinite(parseFloat(geoHeight)) ? parseFloat(geoHeight) / 100 : 0.05;
    const fallbackRotation = Number.isFinite(parseFloat(geoRotation)) ? parseFloat(geoRotation) : 0;
    const fallbackFillOpacity = Number.isFinite(parseFloat(geoFillOpacity)) ? parseFloat(geoFillOpacity) : 0.14;
    const fallbackStrokeWidth = Number.isFinite(parseFloat(geoStrokeWidth)) ? parseFloat(geoStrokeWidth) : 2;
    const fallbackOrder = Number.isFinite(parseInt(geoOrder, 10)) ? parseInt(geoOrder, 10) : 4;
    const rep: AssetRepresentation = {
      type: geoShape,
      lat: Number(existing.lat ?? fallbackLat),
      lon: Number(existing.lon ?? fallbackLon),
      width: Number(existing.width ?? fallbackWidth),
      height: Number(existing.height ?? fallbackHeight),
      rotation: Number(existing.rotation ?? fallbackRotation),
      strokeColor: existing.strokeColor ?? geoStrokeColor,
      fillColor: existing.fillColor ?? geoFillColor,
      fillOpacity: Number(existing.fillOpacity ?? fallbackFillOpacity),
      strokeWidth: Number(existing.strokeWidth ?? fallbackStrokeWidth),
      zIndex: Number(existing.zIndex ?? fallbackOrder),
      pointShape: existing.pointShape ?? geoPointShape,
    };
    void handleRepresentationChange(selectedAssetId, rep);
  }

  async function handleCreateAsset() {
    if (!activeTenantId || !newAsset.label.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const row = await createAsset(activeTenantId, {
        label: newAsset.label.trim(),
        lor: newAsset.lor,
        parent_id: newAsset.parent_id || null,
        node_type: newAsset.node_type || defaultNodeType(newAsset.lor),
      });
      setNewAsset({ label: '', lor: 3, parent_id: '', node_type: 'platform', domain: '', axis: 'spatial', boundary_owner: 'admin-registry', path_desc: '' });
      setDrawerOpen(false);
      await loadTree(activeTenantId);
      setSelectedAssetId(row.id);
      setToast(`Created asset "${row.label}"`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create asset');
    } finally {
      setBusy(false);
    }
  }

  async function handleRepresentationChange(assetId: string, representation: AssetRepresentation) {
    if (!activeTenantId) return;
    const bbox = bboxFromRepresentation(representation);
    setTree((nodes) => {
      const patch = (n: AssetTreeNode): AssetTreeNode => {
        if (n.id === assetId) return { ...n, bbox, representation };
        return { ...n, children: n.children?.map(patch) ?? [] };
      };
      return nodes.map(patch);
    });
    try {
      await updateAsset(activeTenantId, assetId, { bbox, representation });
      setToast('Geometry persisted');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to persist geometry');
    }
  }

  async function handleOpenViewer() {
    const target = selectedAsset?.gtwin_id || activeTenant?.estate_gtwin_id;
    if (!target) { setError('No gTwin associated yet.'); return; }
    navigate(`/t/${target}`);
  }

  async function handleVerifyScope() {
    if (!activeTenantId || !activeTenant?.estate_gtwin_id) return;
    setBusy(true);
    try {
      await fetchScopeManifest(activeTenantId, activeTenant.estate_gtwin_id);
      setToast('Scope manifest OK');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Scope manifest failed');
    } finally {
      setBusy(false);
    }
  }

  function exportGeoJson() {
    const features = flatTree
      .filter((r) => r.bbox && r.bbox.length === 4)
      .map((r) => ({
        type: 'Feature',
        properties: { assetId: r.id, label: r.label, lor: r.lor },
        geometry: {
          type: 'Polygon',
          coordinates: [[
            [r.bbox![0], r.bbox![1]], [r.bbox![2], r.bbox![1]],
            [r.bbox![2], r.bbox![3]], [r.bbox![0], r.bbox![3]],
            [r.bbox![0], r.bbox![1]],
          ]],
        },
      }));
    const blob = new Blob([JSON.stringify({ type: 'FeatureCollection', features }, null, 2)], { type: 'application/geo+json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${activeTenantId ?? 'estate'}-footprints.geojson`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  function exportHierarchyCsv() {
    const escape = (value: unknown) => {
      const text = value == null ? '' : String(value);
      return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const columns = [
      'tenantId', 'tenantCode', 'assetId', 'parentId', 'label', 'lor', 'nodeType', 'path',
      'geometryType', 'lat', 'lon', 'width', 'height', 'rotation', 'strokeWidth',
      'strokeColor', 'fillColor', 'fillOpacity', 'displayOrder', 'pointShape', 'coordinates', 'bbox',
    ];
    const rows = flatTree.map((asset) => {
      const rep = (asset.representation ?? {}) as Partial<AssetRepresentation>;
      return [
        activeTenantId,
        activeTenant?.code ?? '',
        asset.id,
        asset.parent_id ?? '',
        asset.label,
        asset.lor,
        asset.node_type,
        asset.path ?? '',
        rep.type ?? '',
        rep.lat ?? '',
        rep.lon ?? '',
        rep.width ?? '',
        rep.height ?? '',
        rep.rotation ?? '',
        rep.strokeWidth ?? '',
        rep.strokeColor ?? '',
        rep.fillColor ?? '',
        rep.fillOpacity ?? '',
        rep.zIndex ?? '',
        rep.pointShape ?? '',
        rep.coordinates?.map((point) => point.join(':')).join('|') ?? '',
        asset.bbox?.join('|') ?? '',
      ].map(escape).join(',');
    });
    const blob = new Blob([[columns.join(','), ...rows].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${activeTenant?.code ?? activeTenantId ?? 'estate'}-hierarchy-geometry.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    setToast('Exported hierarchy CSV with geometry details');
  }

  async function importGeoJson(file: File | undefined) {
    if (!file || !activeTenantId) return;
    const text = await file.text();
    const parsed = JSON.parse(text) as { features?: Array<{ properties?: Record<string, unknown>; geometry?: { coordinates?: number[][][] } }> };
    for (const f of parsed.features ?? []) {
      const assetId = f.properties?.assetId;
      const ring = f.geometry?.coordinates?.[0];
      if (typeof assetId !== 'string' || !ring?.length) continue;
      const xs = ring.map((p) => p[0]);
      const ys = ring.map((p) => p[1]);
      const bbox = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
      await updateAsset(activeTenantId, assetId, { bbox });
    }
    await loadTree(activeTenantId);
    setToast('Imported GeoJSON footprints');
  }

  const layerRefs = (selectedAsset?.layer_refs ?? []) as Array<Record<string, unknown>>;
  const projects = ((selectedAsset?.metadata?.projects ?? []) as Array<{ id: string; label: string }>);
  const lor3AssetIndex = useMemo(() => new Map(lor3Assets.map((asset) => [asset.id, asset])), [lor3Assets]);
  const selectedLayer = useMemo(
    () => emittedLayers.find((layer) => layer.id === selectedLayerId) ?? null,
    [emittedLayers, selectedLayerId],
  );
  const selectedAssociationProject = useMemo(
    () => gtwinProjects.find((project) => project.id === associationProjectId) ?? null,
    [associationProjectId, gtwinProjects],
  );
  const selectedAssociationAsset = useMemo(
    () => lor3AssetIndex.get(associationAssetId) ?? null,
    [associationAssetId, lor3AssetIndex],
  );
  const layerStatusCounts = useMemo(() => {
    return emittedLayers.reduce<Record<LayerAssociationStatus, number>>((acc, layer) => {
      acc[layerStatusFor(layer, flatTree)] += 1;
      return acc;
    }, { independent: 0, 'project-linked': 0, 'asset-associated': 0 });
  }, [emittedLayers, flatTree]);
  const layerTypeOptions = useMemo(
    () => Array.from(new Set(emittedLayers.map((layer) => layer.layerType))).sort(),
    [emittedLayers],
  );
  const filteredEmittedLayers = useMemo(() => {
    const term = layerQuery.trim().toLowerCase();
    return emittedLayers.filter((layer) => {
      const status = layerStatusFor(layer, flatTree);
      const associatedAsset = associatedAssetFor(layer, flatTree);
      if (layerTypeFilter !== 'all' && layer.layerType !== layerTypeFilter) return false;
      if (layerStatusFilter !== 'all' && status !== layerStatusFilter) return false;
      if (!withinDateFilter(layer, layerDateFilter)) return false;
      if (!term) return true;
      const haystack = [
        layer.name,
        layer.code,
        layer.typeLabel,
        layer.crs,
        layer.center,
        status,
        associatedAsset?.label,
        associatedAsset?.id,
      ].join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [emittedLayers, flatTree, layerDateFilter, layerQuery, layerStatusFilter, layerTypeFilter]);

  function openAssociation(layer: EmittedLayerRecord) {
    setSelectedLayerId(layer.id);
    setAssociationProjectId((current) => current || gtwinProjects[0]?.id || activeTenant?.estate_gtwin_id || '');
    setAssociationAssetId((current) => {
      if (selectedAsset?.lor === 3) return selectedAsset.id;
      return current || lor3Assets[0]?.id || '';
    });
    setAssociationOpen(true);
  }

  async function handleAssociateLayer() {
    if (!activeTenantId || !selectedLayer || !associationProjectId || !associationAssetId) {
      setLayerError('Select an emitted layer, gTwin project, and LoR3 asset first.');
      return;
    }
    setBusy(true);
    setLayerError(null);
    try {
      await associateAssetLayer(activeTenantId, associationAssetId, {
        gtwinId: associationProjectId,
        sceneItemId: null,
        slot: 'converter',
        layerKind: selectedLayer.layerType,
        sourceRunId: selectedLayer.id,
        label: selectedLayer.name,
        metadata: {
          outputPath: selectedLayer.path,
          artifactUrl: selectedLayer.url,
          typeLabel: selectedLayer.typeLabel,
          size: selectedLayer.size,
          mtime: selectedLayer.mtime,
          crs: selectedLayer.crs,
          center: selectedLayer.center,
          bbox: selectedLayer.bbox,
          projectLabel: selectedAssociationProject?.label ?? associationProjectId,
          assetLabel: selectedAssociationAsset?.label ?? associationAssetId,
        },
      });
      await Promise.all([
        loadTree(activeTenantId),
        loadLayerManagementData(activeTenantId),
      ]);
      setAssociationOpen(false);
      setToast(`Associated ${selectedLayer.name}`);
    } catch (err) {
      setLayerError(err instanceof Error ? err.message : 'Failed to associate layer');
    } finally {
      setBusy(false);
    }
  }

  async function addProjectReference() {
    if (!activeTenantId || !selectedAsset || !projectLabel.trim()) return;
    const nextProjects = [...projects, { id: `project-${Date.now()}`, label: projectLabel.trim() }];
    await updateAsset(activeTenantId, selectedAsset.id, { metadata: { ...(selectedAsset.metadata ?? {}), projects: nextProjects } });
    setProjectLabel('');
    await loadTree(activeTenantId);
    setToast('Project reference stored');
  }

  return (
    <main className="estate-v5" data-route="/admin/estate">
      {/* ── Topbar ── */}
      <header className="estate-topbar">
        <div className="estate-brand">
          <button className="estate-menu-button" type="button" onClick={() => setGlobalNavOpen(true)} aria-label="Open navigation">
            <span className="material-symbols-outlined">menu</span>
          </button>
          <span className="estate-logo">GeoSoft</span>
          <span className="estate-badge accent">LoR3 Estate</span>
        </div>
        <select className="estate-top-tenant" value={activeTenantId ?? ''} onChange={(e) => setActiveTenantId(e.currentTarget.value || null)}>
          <option value="">Select tenant</option>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <nav className="estate-nav-tabs">
          {(['assets', 'layers', 'viewer', 'projects', 'handover'] as ScreenId[]).map((id) => (
            <button key={id} type="button" className={screen === id ? 'active' : ''} onClick={() => setScreen(id)}>
              {id}
            </button>
          ))}
        </nav>
        <div className="estate-top-actions">
          <button type="button" onClick={handleVerifyScope} disabled={busy || !activeTenant?.estate_gtwin_id}>Verify scope</button>
          <button type="button" className="primary" onClick={handleOpenViewer}>Open real viewer</button>
        </div>
      </header>

      {/* ── Global nav overlay ── */}
      {globalNavOpen && (
        <div className="estate-global-nav" role="dialog" aria-modal="true">
          <button className="estate-global-scrim" type="button" aria-label="Close" onClick={() => setGlobalNavOpen(false)} />
          <aside className="estate-global-panel">
            <div className="estate-global-header">
              <div className="estate-brand">
                <span className="estate-logo">GeoSoft</span>
                <span className="estate-badge accent">LoR3 Estate</span>
              </div>
              <button type="button" className="estate-global-close" aria-label="Close navigation" onClick={() => setGlobalNavOpen(false)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <button className="estate-global-item" type="button" onClick={() => { setScreen('assets'); setGlobalNavOpen(false); }}><span className="material-symbols-outlined">account_tree</span>Asset hierarchy</button>
            <button className="estate-global-item" type="button" onClick={() => { setScreen('layers'); setGlobalNavOpen(false); }}><span className="material-symbols-outlined">layers</span>gTwins and layers</button>
            <button className="estate-global-item" type="button" onClick={() => { setScreen('viewer'); setGlobalNavOpen(false); }}><span className="material-symbols-outlined">visibility</span>User viewer</button>
            <Link className="estate-global-item" to="/" onClick={() => setGlobalNavOpen(false)}><span className="material-symbols-outlined">dashboard</span>Platform landing</Link>
            <Link className="estate-global-item" to="/projects" onClick={() => setGlobalNavOpen(false)}><span className="material-symbols-outlined">folder_open</span>Projects hub</Link>
          </aside>
        </div>
      )}

      {/* ── Error / Toast ── */}
      {error && (
        <div className="estate-error">
          <strong>Error</strong>
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      {toast && <div className="estate-toast">{toast}</div>}

      {/* ── Assets screen ── */}
      {screen === 'assets' && (
        <section className={`estate-shell${treeCollapsed ? ' tree-pane-collapsed' : ''}`}>
          <button className="estate-panel-restore" type="button" onClick={() => setTreeCollapsed(false)}>
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>chevron_right</span>
            Hierarchy
          </button>

          <aside className="estate-side-panel">
            {/* Tree header */}
            <div className="estate-tree-header">
              <div className="estate-tree-header-left">
                <button className="estate-collapse-btn" type="button" onClick={() => setTreeCollapsed(true)} title="Collapse panel">
                  <span className="material-symbols-outlined">chevron_left</span>
                </button>
                <div className="estate-tree-title">
                  <h3>Asset hierarchy</h3>
                  <small>Docked panel · not a temporary overlay</small>
                </div>
              </div>
              <div className="estate-tree-actions">
                <button type="button" onClick={expandAll}>Expand</button>
                <button type="button" onClick={collapseAll}>Collapse</button>
                <button type="button" className="primary" onClick={() => { setNewAsset((p) => ({ ...p, parent_id: selectedAssetId ?? '' })); setDrawerOpen(true); }}>Add</button>
              </div>
            </div>

            {/* Filter */}
            <div className="estate-tree-toolbar">
              <input placeholder="Filter tree..." value={filter} onChange={(e) => setFilter(e.currentTarget.value)} />
            </div>

            {/* Tree body */}
            <div className="estate-tree-body">
              {filteredTree.map((node) => (
                <TreeNode
                  key={node.id}
                  node={node}
                  selectedId={selectedAssetId}
                  onSelect={setSelectedAssetId}
                  openNodes={openNodes}
                  onToggle={toggleNode}
                />
              ))}
              {!filteredTree.length && <p className="estate-empty">No assets found.</p>}
            </div>

            <div className="estate-asset-actions">
              <div>
                <strong>{selectedAsset?.label ?? 'No asset selected'}</strong>
                <small>
                  {selectedAsset
                    ? `LoR${selectedAsset.lor} · ${selectedAsset.node_type} · ${layerRefs.length} layer${layerRefs.length !== 1 ? 's' : ''}`
                    : 'Select a hierarchy node to manage actions'}
                </small>
              </div>
              <div className="estate-asset-action-row">
                <button type="button" className="primary" onClick={() => { setNewAsset((p) => ({ ...p, parent_id: selectedAssetId ?? '' })); setDrawerOpen(true); }}>
                  <span className="material-symbols-outlined">add</span>
                  Add asset
                </button>
                <button type="button" onClick={() => setScreen('layers')}>
                  <span className="material-symbols-outlined">layers</span>
                  Add layer
                </button>
                <button type="button" onClick={handleOpenViewer}>
                  <span className="material-symbols-outlined">open_in_new</span>
                  LoR4+ detail
                </button>
              </div>
            </div>
          </aside>

          {/* Map panel */}
          <main className="estate-map-panel">
            <div className="estate-basemap-ribbon">
              <span>BASEMAP</span>
              <strong>{basemapLabel}</strong>
              <span>{activeTenant?.region ?? ''} · EPSG:4326</span>
              <div className="estate-ribbon-actions">
                <div className="estate-map-tool-anchor">
                  <button type="button" className="estate-map-tool-trigger" onClick={() => setToolPaletteOpen((open) => !open)} title="Add map element">
                    <span className="material-symbols-outlined">add_location_alt</span>
                    {mapTool === 'select' ? 'Select' : `Place ${mapTool}`}
                  </button>
                  {toolPaletteOpen && (
                    <div className="estate-map-tool-popover">
                      {(['select', 'point', 'rectangle', 'square', 'polygon', 'area', 'corridor', 'path'] as EstateMapTool[]).map((tool) => (
                        <button
                          key={tool}
                          type="button"
                          className={mapTool === tool ? 'active' : ''}
                          onClick={() => { setMapTool(tool); setToolPaletteOpen(false); }}
                        >
                          <span className="material-symbols-outlined">
                            {tool === 'select' ? 'ads_click' : tool === 'point' ? 'location_on' : tool === 'path' ? 'timeline' : tool === 'corridor' ? 'route' : 'category'}
                          </span>
                          {tool}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className={`estate-map-config-toggle${mapConfigOpen ? ' active' : ''}`}
                  onClick={() => setMapConfigOpen((o) => !o)}
                >
                  {mapConfigOpen ? 'Close' : 'Map & geometry setup'}
                </button>
              </div>
            </div>
            <div className="estate-map-shell">
              {mapTool !== 'select' && (
                <div className="estate-placement-hint">
                  <span className="material-symbols-outlined">edit_location_alt</span>
                  <span>
                    {['polygon', 'area', 'path', 'corridor'].includes(mapTool)
                      ? `Click multiple points to draw ${mapTool}`
                      : `Click map to place ${mapTool}`}
                  </span>
                  <button type="button" onClick={() => setMapTool('select')}>Esc</button>
                </div>
              )}
              <EstateBasemapPanel
                tree={tree}
                selectedId={selectedAssetId}
                tool={mapTool}
                basemap={basemap}
                onSelect={setSelectedAssetId}
                onRepresentationChange={handleRepresentationChange}
              />
              {mapConfigOpen && (
                <aside className="estate-map-configurator">
                  <strong>Map & geometry setup</strong>
                  <small style={{ color: 'var(--ink3)' }}>Single panel — basemap context plus selected asset controls</small>

                  {/* Basemap & estate section */}
                  <div className="estate-config-section">
                    <span className="estate-config-section-title">Basemap & estate</span>
                    <small style={{ color: 'var(--ink3)' }}>{activeTenant?.region ?? 'No region'} · EPSG:4326</small>
                    <label>
                      Estate profile
                      <select value={estateProfile} onChange={(e) => setEstateProfile(e.target.value)}>
                        <option value="oil-gas">Oil & gas estate</option>
                        <option value="smart-city">Smart city</option>
                        <option value="renewables">Renewables</option>
                        <option value="generic">Generic</option>
                      </select>
                    </label>
                    <label>
                      Background map
                      <select value={basemap} onChange={(e) => setBasemap(e.currentTarget.value as typeof basemap)}>
                        <option value="none">No basemap / designer canvas</option>
                        <option value="osm">OpenStreetMap</option>
                        <option value="carto-positron">Carto Positron</option>
                        <option value="carto-dark-matter">Carto Dark Matter</option>
                        <option value="esri-imagery">ESRI Imagery</option>
                      </select>
                    </label>
                    <label>
                      Placement template
                      <select>
                        <option value="">Manual / custom</option>
                        <option value="grid-4x3">Grid 4×3</option>
                        <option value="radial">Radial</option>
                      </select>
                    </label>
                    <div className="estate-config-actions">
                      <button type="button" onClick={exportGeoJson}>Export GeoJSON</button>
                      <button type="button" onClick={exportHierarchyCsv}>Export hierarchy CSV</button>
                      <button type="button" onClick={() => importInputRef.current?.click()}>Import GeoJSON</button>
                      <input ref={importInputRef} hidden type="file" accept=".geojson,.json" onChange={(e) => void importGeoJson(e.currentTarget.files?.[0])} />
                    </div>
                  </div>

                  {/* Selected asset geometry section */}
                  <div className="estate-config-section">
                    {selectedAsset ? (
                      <>
                        <span className="estate-config-section-title">Selected asset · {selectedAsset.label}</span>
                        <small style={{ color: 'var(--ink3)' }}>
                          Point/rectangle/square insert on click. Polygon/area/path/corridor draw by clicking multiple vertices.
                        </small>
                        <label>
                          Shape
                          <select value={geoShape} onChange={(e) => setGeoShape(e.target.value as typeof geoShape)}>
                            <option value="rectangle">rectangle</option>
                            <option value="square">square</option>
                            <option value="polygon">polygon</option>
                            <option value="area">area</option>
                            <option value="corridor">corridor</option>
                            <option value="path">path</option>
                            <option value="point">point</option>
                          </select>
                        </label>
                        {geoShape === 'point' && (
                          <label>
                            Point shape
                            <select value={geoPointShape} onChange={(e) => setGeoPointShape(e.target.value as typeof geoPointShape)}>
                              <option value="circle">circle</option>
                              <option value="square">square</option>
                              <option value="diamond">diamond</option>
                              <option value="triangle">triangle</option>
                            </select>
                          </label>
                        )}
                        <div className="estate-geometry-grid">
                          <label>Lat<input type="number" step="0.000001" value={geoLat} onChange={(e) => setGeoLat(e.target.value)} /></label>
                          <label>Lon<input type="number" step="0.000001" value={geoLon} onChange={(e) => setGeoLon(e.target.value)} /></label>
                        </div>
                        {['polygon', 'area', 'path', 'corridor'].includes(geoShape) && (
                          <button type="button" className="estate-config-viewer" onClick={clearDrawnVertices}>
                            Clear drawn vertices
                          </button>
                        )}
                        {geoShape !== 'point' && (
                          <div className="estate-geometry-grid">
                            <label>W %<input type="number" min="2" max="45" step="0.5" value={geoWidth} onChange={(e) => setGeoWidth(e.target.value)} /></label>
                            {geoShape !== 'square' && <label>H %<input type="number" min="2" max="45" step="0.5" value={geoHeight} onChange={(e) => setGeoHeight(e.target.value)} /></label>}
                            <label>Rotation°<input type="number" min="-180" max="180" value={geoRotation} onChange={(e) => setGeoRotation(e.target.value)} /></label>
                            <label>Display order<input type="number" min="1" max="20" value={geoOrder} onChange={(e) => setGeoOrder(e.target.value)} /></label>
                          </div>
                        )}
                        <label>
                          Stroke width <span style={{ color: 'var(--accent)' }}>{geoStrokeWidth}px</span>
                          <input type="range" min="1" max="8" step="1" value={geoStrokeWidth} onChange={(e) => setGeoStrokeWidth(e.target.value)} style={{ width: '100%' }} />
                        </label>
                        <label>
                          Fill opacity <span style={{ color: 'var(--accent)' }}>{Math.round(parseFloat(geoFillOpacity) * 100)}%</span>
                          <input type="range" min="0.04" max="0.6" step="0.02" value={geoFillOpacity} onChange={(e) => setGeoFillOpacity(e.target.value)} style={{ width: '100%' }} />
                        </label>
                        <div className="estate-geometry-grid">
                          <label>Stroke color<input type="color" value={geoStrokeColor} onChange={(e) => setGeoStrokeColor(e.target.value)} /></label>
                          <label>Fill color<input type="color" value={geoFillColor} onChange={(e) => setGeoFillColor(e.target.value)} /></label>
                        </div>
                        <button type="button" className="estate-config-apply" onClick={applyGeometryToAsset}>Apply to selected asset</button>
                        <button type="button" className="estate-config-viewer" onClick={handleOpenViewer}>Open LoR4+ detail viewer</button>
                      </>
                    ) : (
                      <>
                        <span className="estate-config-section-title">Selected asset</span>
                        <small style={{ color: 'var(--ink3)' }}>Select a hierarchy node to edit placement and geometry style.</small>
                      </>
                    )}
                  </div>
                </aside>
              )}
            </div>
          </main>
        </section>
      )}

      {/* ── Config drawer (Add asset) ── */}
      <div className={`estate-drawer-scrim${drawerOpen ? ' open' : ''}`} onClick={() => setDrawerOpen(false)} />
      <aside className={`estate-config-drawer${drawerOpen ? ' open' : ''}`}>
        <div className="estate-drawer-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="estate-badge accent" style={{ fontSize: 11 }}>Configuration</span>
            </div>
            <h3 style={{ marginTop: 8 }}>Add asset</h3>
            <p>Create a new node in the admin hierarchy tree.</p>
          </div>
          <button className="estate-drawer-close" type="button" onClick={() => setDrawerOpen(false)}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <div className="estate-drawer-body">
          <div className="estate-drawer-form">
            <div className="field-row">
              <label>
                Asset label
                <input value={newAsset.label} onChange={(e) => setNewAsset((p) => ({ ...p, label: e.target.value }))} placeholder="Pipeline Segment 17" />
              </label>
              <label>
                LoR / ownership boundary
                <select value={newAsset.lor} onChange={(e) => setNewAsset((p) => ({ ...p, lor: Number(e.target.value) }))}>
                  <option value={0}>LoR0 · portfolio / region</option>
                  <option value={1}>LoR1 · business area</option>
                  <option value={2}>LoR2 · field / location</option>
                  <option value={3}>LoR3 · addressable asset shell</option>
                </select>
              </label>
            </div>
            <label>
              Asset type
              <select value={newAsset.node_type} onChange={(e) => setNewAsset((p) => ({ ...p, node_type: e.target.value }))}>
                <option value="portfolio">portfolio</option>
                <option value="business-unit">business-unit</option>
                <option value="field">field</option>
                <option value="location">location</option>
                <option value="platform">platform</option>
                <option value="complex-container">complex-container</option>
                <option value="terminal">terminal</option>
                <option value="cluster">cluster</option>
                <option value="development-package">development-package</option>
                <option value="floating-production-asset">floating-production-asset</option>
                <option value="pipeline-segment">pipeline-segment</option>
              </select>
            </label>
            <div className="field-row">
              <label>
                Spatial / containment parent
                <select value={newAsset.parent_id} onChange={(e) => setNewAsset((p) => ({ ...p, parent_id: e.target.value }))}>
                  <option value="">No parent / root</option>
                  {flatTree.map((r) => <option key={r.id} value={r.id}>{r.label} (LoR{r.lor})</option>)}
                </select>
              </label>
              <label>
                Hierarchy axis
                <select value={newAsset.axis} onChange={(e) => setNewAsset((p) => ({ ...p, axis: e.target.value }))}>
                  <option value="spatial">spatial</option>
                  <option value="functional">functional</option>
                  <option value="process">process</option>
                </select>
              </label>
            </div>
            <div className="field-row">
              <label>
                Boundary owner
                <select value={newAsset.boundary_owner} onChange={(e) => setNewAsset((p) => ({ ...p, boundary_owner: e.target.value }))}>
                  <option value="admin-registry">admin-registry</option>
                  <option value="admin-registry-plus-gtwin-shell">admin-registry + gTwin shell</option>
                  <option value="gtwin-layer-object-index">gtwin-layer-object-index</option>
                  <option value="hybrid-admin-shell-layer-interior">hybrid-admin-shell-layer-interior</option>
                </select>
              </label>
              <label>
                Domain
                <select value={newAsset.domain} onChange={(e) => setNewAsset((p) => ({ ...p, domain: e.target.value }))}>
                  <option value="">No domain</option>
                  <option value="Asset Registry">Asset Registry (asset)</option>
                  <option value="Plant Engineering">Plant Engineering (engineering)</option>
                  <option value="Subsea">Subsea (subsea)</option>
                  <option value="Geospatial">Geospatial (geospatial)</option>
                </select>
              </label>
            </div>
            <label>
              Location / path description
              <input value={newAsset.path_desc} onChange={(e) => setNewAsset((p) => ({ ...p, path_desc: e.target.value }))} placeholder="Field A / Export Pipeline / Segment 17" />
            </label>
            <button className="estate-drawer-submit" type="button" onClick={handleCreateAsset} disabled={busy || !activeTenantId || !newAsset.label.trim()}>
              Create asset node
            </button>
          </div>
        </div>
      </aside>

      {/* ── Layers screen ── */}
      {screen === 'layers' && (
        <section className="estate-screen-panel estate-layer-management">
          <div className="estate-screen-heading">
            <div>
              <h2>Layer management</h2>
              <p>Converter-emitted layers are managed separately from gTwin projects, then explicitly mapped to LoR3 assets.</p>
            </div>
            <div className="estate-layer-heading-actions">
              <button type="button" onClick={() => activeTenantId && void loadLayerManagementData(activeTenantId)} disabled={layersLoading || !activeTenantId}>
                {layersLoading ? 'Refreshing...' : 'Refresh live lists'}
              </button>
              <button type="button" onClick={() => window.open('http://localhost:5174/job-config', '_blank', 'noreferrer')}>
                Open converter job config
              </button>
            </div>
          </div>

          {layerError && (
            <div className="estate-layer-error">
              <strong>Layer inventory error</strong>
              <span>{layerError}</span>
            </div>
          )}

          <div className="estate-layer-summary">
            <div className="estate-layer-stat">
              <span>gTwin projects</span>
              <strong>{gtwinProjects.length}</strong>
              <small>Live from :3001</small>
            </div>
            <div className="estate-layer-stat">
              <span>Emitted layers</span>
              <strong>{emittedLayers.length}</strong>
              <small>Live from :3011</small>
            </div>
            <div className="estate-layer-stat">
              <span>Independent</span>
              <strong>{layerStatusCounts.independent}</strong>
              <small>Needs project or asset mapping</small>
            </div>
            <div className="estate-layer-stat">
              <span>LoR3 associations</span>
              <strong>{layerStatusCounts['asset-associated']}</strong>
              <small>{lor3Assets.length} LoR3 attach points</small>
            </div>
          </div>

          <div className="estate-layer-controls">
            <input
              value={layerQuery}
              onChange={(event) => setLayerQuery(event.currentTarget.value)}
              placeholder="Search emitted layers, CRS, path, target asset..."
            />
            <select value={layerTypeFilter} onChange={(event) => setLayerTypeFilter(event.currentTarget.value as 'all' | EmittedLayerType)}>
              <option value="all">All layer types</option>
              {layerTypeOptions.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
            <select value={layerStatusFilter} onChange={(event) => setLayerStatusFilter(event.currentTarget.value as 'all' | LayerAssociationStatus)}>
              <option value="all">All statuses</option>
              <option value="independent">Independent</option>
              <option value="project-linked">Project-linked</option>
              <option value="asset-associated">Asset-associated</option>
            </select>
            <select value={layerDateFilter} onChange={(event) => setLayerDateFilter(event.currentTarget.value as LayerDateFilter)}>
              <option value="all">Any date</option>
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
            <div className="estate-layer-view-toggle">
              <button type="button" className={layerView === 'list' ? 'active' : ''} onClick={() => setLayerView('list')}>List</button>
              <button type="button" className={layerView === 'cards' ? 'active' : ''} onClick={() => setLayerView('cards')}>Cards</button>
            </div>
          </div>

          <div className={`estate-emitted-layer-inventory ${layerView}`}>
            {filteredEmittedLayers.map((layer) => {
              const status = layerStatusFor(layer, flatTree);
              const associatedAsset = associatedAssetFor(layer, flatTree);
              return (
                <article
                  key={layer.id}
                  className={`estate-emitted-layer ${selectedLayerId === layer.id ? 'selected' : ''}`}
                  data-status={status}
                >
                  <button type="button" className="estate-layer-select" onClick={() => setSelectedLayerId(layer.id)}>
                    <span className="estate-layer-type">{layer.typeLabel}</span>
                    <strong>{layer.name}</strong>
                    <small>{layer.code}</small>
                  </button>
                  <div className="estate-layer-metadata">
                    <span>{layer.dateLabel}</span>
                    <span>{layer.sizeLabel}</span>
                    <span>{layer.crs}</span>
                    <span>{layer.center}</span>
                  </div>
                  <div className="estate-layer-status">
                    <span data-status={status}>{statusLabel(status)}</span>
                    <small>{associatedAsset ? `${associatedAsset.label} · ${associatedAsset.id}` : 'No LoR3 asset mapped'}</small>
                  </div>
                  <button type="button" className="estate-layer-associate" onClick={() => openAssociation(layer)}>
                    {status === 'asset-associated' ? 'Review mapping' : 'Associate'}
                  </button>
                </article>
              );
            })}
            {!layersLoading && filteredEmittedLayers.length === 0 && (
              <div className="estate-layer-empty">
                <strong>No emitted layers match the current filters.</strong>
                <small>Refresh the live converter list or clear filters.</small>
              </div>
            )}
            {layersLoading && (
              <div className="estate-layer-empty">
                <strong>Loading live project and converter inventories...</strong>
                <small>Reading :3001 gTwin projects, :3011 emitted layers, and LoR3 assets.</small>
              </div>
            )}
          </div>

          {associationOpen && selectedLayer && (
            <aside className="estate-layer-association-drawer">
              <div className="estate-layer-association-header">
                <div>
                  <strong>Associate emitted layer</strong>
                  <small>{selectedLayer.name}</small>
                </div>
                <button type="button" onClick={() => setAssociationOpen(false)} aria-label="Close association drawer">
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <div className="estate-layer-association-body">
                <div className="estate-layer-association-preview">
                  <span>{selectedLayer.typeLabel}</span>
                  <strong>{selectedLayer.code}</strong>
                  <small>{selectedLayer.sizeLabel} · {selectedLayer.crs} · {selectedLayer.center}</small>
                </div>
                <label>
                  gTwin project target
                  <select value={associationProjectId} onChange={(event) => setAssociationProjectId(event.currentTarget.value)}>
                    <option value="">Select project</option>
                    {gtwinProjects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.label} · {project.layerCount} layers
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  LoR3 asset target
                  <select value={associationAssetId} onChange={(event) => setAssociationAssetId(event.currentTarget.value)}>
                    <option value="">Select LoR3 asset</option>
                    {lor3Assets.map((asset) => (
                      <option key={asset.id} value={asset.id}>
                        {asset.label} · {asset.path || asset.id}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="estate-layer-association-note">
                  This writes a canonical LoR3 `layer_refs` association. The emitted layer remains separate from the gTwin project inventory unless it is later pinned as a scene item.
                </div>
                <button
                  type="button"
                  className="primary"
                  onClick={() => void handleAssociateLayer()}
                  disabled={busy || !associationProjectId || !associationAssetId}
                >
                  {busy ? 'Associating...' : 'Associate layer'}
                </button>
              </div>
            </aside>
          )}
        </section>
      )}

      {/* ── Viewer screen ── */}
      {screen === 'viewer' && (
        <section className="estate-screen-panel estate-viewer-bridge">
          <h2>Operational Viewer Workspace</h2>
          <p>Open the selected asset or estate gTwin in the real GeoViewer.</p>
          <button type="button" className="primary" onClick={handleOpenViewer}>Open TwinShell viewer</button>
        </section>
      )}

      {/* ── Projects screen ── */}
      {screen === 'projects' && (
        <section className="estate-screen-panel">
          <h2>Analysis projects</h2>
          <p>Projects reference canonical tenant layers and own overlays and saved views only.</p>
          {selectedAsset ? (
            <div className="estate-project-form">
              <input placeholder="Project label" value={projectLabel} onChange={(e) => setProjectLabel(e.currentTarget.value)} />
              <button type="button" onClick={() => void addProjectReference()}>Add reference</button>
              {projects.map((p) => (
                <div key={p.id} className="estate-card">
                  <strong>{p.label}</strong>
                  <small style={{ color: 'var(--ink3)' }}>References {selectedAsset.label}</small>
                </div>
              ))}
            </div>
          ) : <p style={{ marginTop: 12 }}>Select an asset to manage project references.</p>}
        </section>
      )}

      {/* ── Handover screen ── */}
      {screen === 'handover' && (
        <section className="estate-screen-panel">
          <h2>Engineer handover</h2>
          <p>V5 is now implemented as a React route backed by platform APIs. The static HTML remains a visual reference only.</p>
        </section>
      )}
    </main>
  );
}

export default EstateAdminRoute;
